import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth, homeForRole } from "../../context/AuthContext";
import { Logo } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { usePageMeta } from "../../lib/seo";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function PasswordField({ id, value, onChange, testId, minLength }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative mt-1">
      <Input id={id} type={visible ? "text" : "password"} value={value} onChange={onChange}
             required minLength={minLength} data-testid={testId} className="pr-10" />
      <button type="button" onClick={() => setVisible((v) => !v)}
              aria-label={visible ? "Hide password" : "Show password"}
              data-testid={`${testId}-toggle`}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-200">
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

function AuthShell({ title, subtitle, children }) {
  const path = window.location.pathname;
  usePageMeta(`${title} | Servantage`, path, !/^\/(login|register)$/.test(path));
  return (
    <div className="grid min-h-screen md:grid-cols-2">
      <div className="hidden flex-col justify-between bg-[#1E1B4B] p-12 text-white md:flex">
        <Logo light />
        <div>
          <h1 className="font-head text-4xl font-bold leading-tight tracking-tight">
            Turn frontline feedback into measurable service performance.
          </h1>
          <p className="mt-4 max-w-md text-indigo-200">
            Recognise great service. Identify service gaps. Build better teams — across every location.
          </p>
        </div>
        <p className="text-sm text-indigo-300">Verified service intelligence · POPIA-aligned · Multi-tenant</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm sv-rise">
          <div className="mb-8 md:hidden"><Logo /></div>
          <h2 className="font-head text-2xl font-bold text-slate-900">{title}</h2>
          <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
          <div className="mt-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function Login() {
  const [mode, setMode] = useState("email");
  return (
    <AuthShell title="Welcome back" subtitle="Sign in to your Servantage workspace.">
      <div className="mb-5 grid grid-cols-2 rounded-lg bg-slate-100 p-1 text-sm font-semibold" data-testid="login-mode-toggle">
        {[["email", "Email"], ["phone", "Phone (team members)"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setMode(k)} data-testid={`login-mode-${k}`}
            className={`rounded-md py-2 transition-colors ${mode === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>{l}</button>
        ))}
      </div>
      {mode === "email" ? <EmailLogin /> : <PhoneLogin />}
      <p className="mt-6 text-center text-sm text-slate-500">
        New organisation? <Link to="/register" className="font-semibold text-indigo-600 hover:underline" data-testid="to-register">Create account</Link>
      </p>
    </AuthShell>
  );
}

function PhoneLogin() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const requestCode = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/request", { phone });
      setSent(true);
      toast.success(data.message);
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/verify", { phone, code });
      setUser(data);
      navigate(homeForRole(data.role));
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  const submit = (e) => { e.preventDefault(); sent ? verify() : requestCode(); };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="phone-login-form">
      <p className="text-sm text-slate-500">No work email? Sign in with the mobile number your manager saved on your profile.</p>
      <div>
        <Label htmlFor="phone">Mobile number</Label>
        <Input id="phone" type="tel" value={phone} onChange={(e) => { setPhone(e.target.value); setSent(false); setCode(""); }}
          placeholder="082 123 4567" required data-testid="phone-login-number" className="mt-1" />
      </div>
      {sent && (
        <div>
          <Label htmlFor="otp">6-digit code</Label>
          <Input id="otp" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} required data-testid="phone-login-code"
            className="mt-1 text-center font-mono text-lg tracking-[0.5em]" />
          <button type="button" onClick={requestCode} disabled={busy} className="mt-2 text-xs font-semibold text-indigo-600 hover:underline" data-testid="phone-login-resend">Resend code</button>
        </div>
      )}
      <Button type="submit" disabled={busy || (sent && code.length !== 6)} data-testid="phone-login-submit" className="w-full bg-[#4338CA] hover:bg-[#3730A3]">
        {busy ? "Please wait…" : sent ? "Verify & sign in" : "Send code"}
      </Button>
    </form>
  );
}

function EmailLogin() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      navigate(homeForRole(data.role));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" className="mt-1" />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <PasswordField id="password" value={password} onChange={(e) => setPassword(e.target.value)} testId="login-password" />
        </div>
        <div className="flex justify-end">
          <Link to="/forgot-password" className="text-sm font-semibold text-indigo-600 hover:underline" data-testid="forgot-link">Forgot password?</Link>
        </div>
        <Button type="submit" disabled={busy} data-testid="login-submit" className="w-full bg-[#4338CA] hover:bg-[#3730A3]">
          {busy ? "Signing in…" : "Sign in"}
        </Button>
      </form>
      <div className="my-4 flex items-center gap-3 text-xs text-slate-400">
        <div className="h-px flex-1 bg-slate-200" /> OR <div className="h-px flex-1 bg-slate-200" />
      </div>
      <Button variant="outline" onClick={googleLogin} data-testid="google-login" className="w-full">
        Continue with Google
      </Button>
    </>
  );
}

export function Register() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", organization_name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      toast.success("Workspace created");
      navigate(homeForRole(data.role));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Create your workspace" subtitle="Start measuring service performance in minutes.">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label>Your name</Label>
          <Input value={form.name} onChange={set("name")} required data-testid="register-name" className="mt-1" />
        </div>
        <div>
          <Label>Organisation name</Label>
          <Input value={form.organization_name} onChange={set("organization_name")} required data-testid="register-org" className="mt-1" />
        </div>
        <div>
          <Label>Work email</Label>
          <Input type="email" value={form.email} onChange={set("email")} required data-testid="register-email" className="mt-1" />
        </div>
        <div>
          <Label>Password</Label>
          <Input type="password" value={form.password} onChange={set("password")} required minLength={8} data-testid="register-password" className="mt-1" />
          <p className="mt-1 text-xs text-slate-400">At least 8 characters.</p>
        </div>
        <Button type="submit" disabled={busy} data-testid="register-submit" className="w-full bg-[#4338CA] hover:bg-[#3730A3]">
          {busy ? "Creating…" : "Create workspace"}
        </Button>
      </form>
      <div className="my-4 flex items-center gap-3 text-xs text-slate-400">
        <div className="h-px flex-1 bg-slate-200" /> OR <div className="h-px flex-1 bg-slate-200" />
      </div>
      <Button variant="outline" onClick={googleLogin} data-testid="google-register" className="w-full">
        Continue with Google
      </Button>
      <p className="mt-6 text-center text-sm text-slate-500">
        Already have an account? <Link to="/login" className="font-semibold text-indigo-600 hover:underline" data-testid="to-login">Sign in</Link>
      </p>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell title="Reset your password" subtitle="We'll email you a secure reset link.">
      {sent ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="forgot-sent">
          If that email is registered, a reset link has been sent. Check your inbox.
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email" className="mt-1" />
          </div>
          <Button type="submit" disabled={busy} data-testid="forgot-submit" className="w-full bg-[#4338CA] hover:bg-[#3730A3]">
            {busy ? "Sending…" : "Send reset link"}
          </Button>
        </form>
      )}
      <p className="mt-6 text-center text-sm text-slate-500">
        <Link to="/login" className="font-semibold text-indigo-600 hover:underline" data-testid="back-to-login">Back to sign in</Link>
      </p>
    </AuthShell>
  );
}

export function ResetPassword() {
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please sign in.");
      navigate("/login");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell title="Choose a new password" subtitle="Set a strong password you'll remember.">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label>New password</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} data-testid="reset-password" className="mt-1" />
        </div>
        <Button type="submit" disabled={busy || !token} data-testid="reset-submit" className="w-full bg-[#4338CA] hover:bg-[#3730A3]">
          {busy ? "Updating…" : "Update password"}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-slate-500">
        <Link to="/login" className="font-semibold text-indigo-600 hover:underline" data-testid="reset-back">Back to sign in</Link>
      </p>
    </AuthShell>
  );
}
